import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Megaphone, Plus, Edit, Trash2, Eye, MousePointerClick, ExternalLink, MessageSquare, Upload, Film } from "lucide-react";
import { resolveImageUrl } from "../context/AppSettingsContext";
import { VideoPlayer } from "../lib/video";

const EMPTY = {
  name: "", image_url: "", video_url: "", link: "", description: "",
  active: true, priority: 0, valid_from: "", valid_until: "", branch_ids: [],
  segment_ids: [], notify_push: false,
  offers: [], contact_enabled: false, contact_email: "", contact_phone: "",
};

export default function Sponsors() {
  const [rows, setRows] = useState([]);
  const [segments, setSegments] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/sponsors");
      setRows(data || []);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => {
    load();
    api.get("/segments").then((r) => setSegments(r.data || [])).catch(() => setSegments([]));
  }, []);

  const openNew = () => { setForm(EMPTY); setEditingId(null); setEditOpen(true); };
  const openEdit = (s) => {
    setForm({
      name: s.name || "", image_url: s.image_url || "", video_url: s.video_url || "", link: s.link || "",
      description: s.description || "", active: !!s.active, priority: s.priority || 0,
      valid_from: (s.valid_from || "").slice(0, 10),
      valid_until: (s.valid_until || "").slice(0, 10),
      branch_ids: s.branch_ids || [], segment_ids: s.segment_ids || [],
      notify_push: false,
      offers: s.offers || [],
      contact_enabled: !!s.contact_enabled,
      contact_email: s.contact_email || "",
      contact_phone: s.contact_phone || "",
    });
    setEditingId(s.id);
    setEditOpen(true);
  };
  const [uploading, setUploading] = useState(null); // "image" | "video"
  const uploadImage = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading("image");
    const fd = new FormData(); fd.append("file", f); fd.append("kind", "sponsor");
    try {
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((p) => ({ ...p, image_url: data.url }));
      toast.success("Immagine caricata");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(null); e.target.value = ""; }
  };
  const uploadVideo = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading("video");
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/uploads/video", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((p) => ({ ...p, video_url: data.url }));
      toast.success("Video caricato");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(null); e.target.value = ""; }
  };
  const save = async () => {
    if (!form.name.trim()) return toast.error("Nome obbligatorio");
    try {
      const payload = { ...form, image_url: form.image_url.trim() || null, video_url: form.video_url.trim() || null, link: form.link.trim() || null, valid_from: form.valid_from || null, valid_until: form.valid_until || null };
      const { data } = editingId
        ? await api.patch(`/sponsors/${editingId}`, payload)
        : await api.post("/sponsors", payload);
      setEditOpen(false);
      if (data?.push_sent > 0) {
        toast.success(`Sponsor salvato ✓ · Push inviata a ${data.push_sent} clienti`);
      } else {
        toast.success("Sponsor salvato ✓");
      }
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const del = async (s) => {
    if (!window.confirm(`Eliminare "${s.name}"?`)) return;
    try { await api.delete(`/sponsors/${s.id}`); toast.success("Eliminato"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const [contactsFor, setContactsFor] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [contactDrafts, setContactDrafts] = useState({});
  const [savingContactId, setSavingContactId] = useState(null);
  const [contactFilter, setContactFilter] = useState("all"); // all | new | in_progress | closed
  const [exportRange, setExportRange] = useState({ from: "", to: "" });
  const [exporting, setExporting] = useState(null); // null | "xlsx" | "pdf"
  const exportContacts = async (fmt = "xlsx") => {
    if (!contactsFor) return;
    setExporting(fmt);
    try {
      const params = {};
      if (contactFilter !== "all") params.status_filter = contactFilter;
      if (exportRange.from) params.date_from = exportRange.from;
      if (exportRange.to) params.date_to = exportRange.to;
      const res = await api.get(`/sponsors/${contactsFor.id}/contacts.${fmt}`, { params, responseType: "blob" });
      const mime = fmt === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      const safeName = (contactsFor.name || "sponsor").replace(/[^A-Za-z0-9_-]/g, "_").slice(0, 40);
      a.download = `contatti-sponsor-${safeName}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Export ${fmt.toUpperCase()} scaricato`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setExporting(null); }
  };
  const viewContacts = async (s) => {
    setContactsFor(s);
    try {
      const { data } = await api.get(`/sponsors/${s.id}/contacts`);
      setContacts(data || []);
      const drafts = {};
      (data || []).forEach((c) => {
        drafts[c.id] = { notes: c.notes || "", status: c.status || "new" };
      });
      setContactDrafts(drafts);
    } catch (err) { toast.error(formatApiError(err)); setContacts([]); }
  };
  const patchDraft = (id, patch) =>
    setContactDrafts((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));
  const saveContact = async (c) => {
    const draft = contactDrafts[c.id] || {};
    setSavingContactId(c.id);
    try {
      const { data: updated } = await api.patch(
        `/sponsors/${contactsFor.id}/contacts/${c.id}`,
        { status: draft.status || "new", notes: draft.notes || "" },
      );
      setContacts((prev) => prev.map((x) => (x.id === c.id ? updated : x)));
      toast.success("Richiesta aggiornata");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingContactId(null); }
  };

  const STATUS_META = {
    new: { label: "Nuova", cls: "bg-blue-500/15 text-blue-300 border-blue-500/40" },
    in_progress: { label: "In lavorazione", cls: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
    closed: { label: "Chiusa", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  };

  return (
    <div className="space-y-4 animate-fade-up" data-testid="sponsors-page">
      <div className="flex justify-between items-center">
        <div>
          <span className="pill pill-gold"><Megaphone className="h-3 w-3" /> RICAVO EXTRA</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold mt-3">Sponsor & Banner</h1>
          <p className="text-zinc-400 mt-1 text-sm">
            Vendi spazi pubblicitari nell'area cliente ad aziende locali per generare ricavo extra oltre alla loyalty.
          </p>
        </div>
        <Button data-testid="sponsor-add" onClick={openNew} className="bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]">
          <Plus className="h-4 w-4 mr-1" /> Nuovo sponsor
        </Button>
      </div>

      <div className="grid gap-3">
        {rows.length === 0 ? (
          <div className="card-elev p-10 text-center text-zinc-500 text-sm" data-testid="sponsors-empty">
            Nessuno sponsor configurato. Crea il primo per iniziare a monetizzare l'area cliente.
          </div>
        ) : rows.map((s) => (
          <div key={s.id} data-testid={`sponsor-row-${s.id}`} className={`card-elev p-4 flex items-center gap-3 ${s.active ? "" : "opacity-60"}`}>
            {s.video_url ? (
              <div className="h-16 w-24 rounded-lg border border-zinc-800 overflow-hidden bg-black flex items-center justify-center relative">
                {s.image_url ? <img src={resolveImageUrl(s.image_url)} alt={s.name} className="h-full w-full object-cover opacity-70" /> : null}
                <Film className="h-5 w-5 text-white absolute" />
              </div>
            ) : s.image_url ? (
              <img src={resolveImageUrl(s.image_url)} alt={s.name} className="h-16 w-24 object-cover rounded-lg border border-zinc-800" />
            ) : (
              <div className="h-16 w-24 rounded-lg bg-zinc-900 border border-zinc-800 flex items-center justify-center">
                <Megaphone className="h-6 w-6 text-zinc-600" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className="font-heading font-bold text-lg truncate">{s.name}</div>
                {s.active ? <span className="pill pill-mint text-[9px]">Attivo</span> : <span className="text-[10px] text-zinc-500">Disattivo</span>}
                {s.priority > 0 && <span className="text-[10px] text-amber-300">★ {s.priority}</span>}
              </div>
              {s.description && <div className="text-xs text-zinc-500 truncate">{s.description}</div>}
              <div className="flex gap-3 text-[11px] text-zinc-400 mt-1">
                <span className="flex items-center gap-1"><Eye className="h-3 w-3" /> {s.view_count || 0}</span>
                <span className="flex items-center gap-1"><MousePointerClick className="h-3 w-3" /> {s.click_count || 0} click</span>
                {s.link && <a href={s.link} target="_blank" rel="noreferrer" className="text-blue-400 hover:underline flex items-center gap-1"><ExternalLink className="h-3 w-3" /> Link</a>}
              </div>
            </div>
            <div className="relative">
              <Button data-testid={`sponsor-contacts-${s.id}`} variant="outline" size="sm" onClick={() => viewContacts(s)} className="border-blue-800/50 text-blue-300"><MessageSquare className="h-3 w-3" /></Button>
              {s.new_contacts_count > 0 && (
                <span
                  data-testid={`sponsor-new-badge-${s.id}`}
                  className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center border border-zinc-900 shadow"
                  title={`${s.new_contacts_count} richieste nuove da lavorare`}
                >
                  {s.new_contacts_count > 9 ? "9+" : s.new_contacts_count}
                </span>
              )}
            </div>
            <Button data-testid={`sponsor-edit-${s.id}`} variant="outline" size="sm" onClick={() => openEdit(s)} className="border-zinc-800"><Edit className="h-3 w-3" /></Button>
            <Button data-testid={`sponsor-delete-${s.id}`} variant="outline" size="sm" onClick={() => del(s)} className="border-red-800/50 text-red-400"><Trash2 className="h-3 w-3" /></Button>
          </div>
        ))}
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-w-lg" data-testid="sponsor-edit-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? "Modifica sponsor" : "Nuovo sponsor"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nome / brand*</Label>
              <Input data-testid="sponsor-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-950 border-zinc-800" />
            </div>
            <div>
              <Label>Descrizione (opzionale)</Label>
              <Textarea data-testid="sponsor-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="bg-zinc-950 border-zinc-800" />
            </div>
            <div>
              <Label>Immagine banner</Label>
              <div className="flex gap-2 items-start mt-1">
                {form.image_url && <img src={resolveImageUrl(form.image_url)} alt="" className="h-14 w-24 object-cover rounded-md border border-zinc-800 shrink-0" data-testid="sponsor-image-preview" />}
                <div className="flex-1 space-y-1.5">
                  <Input data-testid="sponsor-image-url" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="bg-zinc-950 border-zinc-800 font-mono text-xs" placeholder="https://... oppure carica un file" />
                  <div className="flex items-center gap-2 flex-wrap">
                    <label className="inline-flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full border border-zinc-700 cursor-pointer hover:bg-zinc-900" data-testid="sponsor-image-upload-label">
                      <Upload className="h-3 w-3" /> {uploading === "image" ? "Carico…" : "Carica immagine"}
                      <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={uploadImage} disabled={!!uploading} data-testid="sponsor-image-upload" />
                    </label>
                    {form.image_url && <button type="button" onClick={() => setForm({ ...form, image_url: "" })} className="text-[11px] text-red-400 hover:underline" data-testid="sponsor-image-remove">Rimuovi</button>}
                  </div>
                </div>
              </div>
              <p className="text-[10px] text-zinc-500 mt-1">Consigliato 800×300px orizzontale, PNG o JPG (max 4 MB).</p>
            </div>
            <div>
              <Label>Video (opzionale)</Label>
              <div className="flex gap-2 items-start mt-1">
                {form.video_url && <div className="h-14 w-24 rounded-md overflow-hidden bg-black shrink-0" data-testid="sponsor-video-preview"><VideoPlayer url={form.video_url} className="h-full w-full object-cover" /></div>}
                <div className="flex-1 space-y-1.5">
                  <Input data-testid="sponsor-video-url" value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} className="bg-zinc-950 border-zinc-800 font-mono text-xs" placeholder="URL YouTube / Vimeo / .mp4 oppure carica un file" />
                  <div className="flex items-center gap-2 flex-wrap">
                    <label className="inline-flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full border border-zinc-700 cursor-pointer hover:bg-zinc-900" data-testid="sponsor-video-upload-label">
                      <Film className="h-3 w-3" /> {uploading === "video" ? "Carico…" : "Carica video"}
                      <input type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={uploadVideo} disabled={!!uploading} data-testid="sponsor-video-upload" />
                    </label>
                    {form.video_url && <button type="button" onClick={() => setForm({ ...form, video_url: "" })} className="text-[11px] text-red-400 hover:underline" data-testid="sponsor-video-remove">Rimuovi</button>}
                  </div>
                </div>
              </div>
              <p className="text-[10px] text-zinc-500 mt-1">Se presente, nel portale cliente il video sostituisce l'immagine (autoplay muto); l'immagine fa da anteprima. File mp4/webm max 20 MB.</p>
            </div>
            <div>
              <Label>Link destinazione (opzionale)</Label>
              <Input data-testid="sponsor-link" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} className="bg-zinc-950 border-zinc-800 font-mono text-xs" placeholder="https://sponsor.it" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Valido dal</Label>
                <Input data-testid="sponsor-valid-from" type="date" value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} className="bg-zinc-950 border-zinc-800" />
              </div>
              <div>
                <Label>Valido al</Label>
                <Input data-testid="sponsor-valid-until" type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} className="bg-zinc-950 border-zinc-800" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Priorità (più alto = prima)</Label>
                <Input data-testid="sponsor-priority" type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value) || 0 })} className="bg-zinc-950 border-zinc-800" />
              </div>
              <div className="flex items-end gap-2 pb-2">
                <Checkbox data-testid="sponsor-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: !!v })} />
                <Label>Sponsor attivo</Label>
              </div>
            </div>

            {/* Segmenti destinatari */}
            <div>
              <Label>Mostra solo ai segmenti (lascia vuoto = tutti i clienti)</Label>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="sponsor-segments">
                {segments.length === 0 && <div className="text-xs text-zinc-500">Nessun segmento configurato — lo sponsor verrà mostrato a tutti i clienti attivi.</div>}
                {segments.map((s) => {
                  const on = (form.segment_ids || []).includes(s.id);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      data-testid={`sponsor-segment-${s.id}`}
                      onClick={() => setForm({
                        ...form,
                        segment_ids: on
                          ? form.segment_ids.filter((x) => x !== s.id)
                          : [...(form.segment_ids || []), s.id],
                      })}
                      className={`text-[11px] px-3 py-1.5 rounded-full border ${on ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-200" : "bg-zinc-950 border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}
                    >
                      {s.name} <span className="opacity-60">({s.member_count || 0})</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Offerte dello sponsor */}
            <div>
              <Label className="flex items-center justify-between">
                <span>🎁 Offerte / promozioni</span>
                <button
                  type="button"
                  data-testid="sponsor-offer-add"
                  onClick={() => setForm({ ...form, offers: [...(form.offers || []), { title: "", description: "", code: "" }] })}
                  className="text-[11px] px-2 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/20"
                >
                  + Aggiungi offerta
                </button>
              </Label>
              <div className="space-y-2 mt-2">
                {(form.offers || []).length === 0 && (
                  <div className="text-xs text-zinc-500 italic">Nessuna offerta — il cliente vedrà solo il banner.</div>
                )}
                {(form.offers || []).map((o, i) => (
                  <div key={i} data-testid={`sponsor-offer-${i}`} className="rounded-lg bg-zinc-950 border border-zinc-800 p-2 space-y-1.5">
                    <div className="flex gap-2">
                      <Input
                        data-testid={`sponsor-offer-title-${i}`}
                        placeholder="Titolo (es. -20% su tutta la linea)"
                        value={o.title || ""}
                        onChange={(e) => {
                          const arr = [...form.offers]; arr[i] = { ...arr[i], title: e.target.value };
                          setForm({ ...form, offers: arr });
                        }}
                        className="bg-zinc-900 border-zinc-800 h-9 text-sm flex-1"
                      />
                      <Input
                        data-testid={`sponsor-offer-code-${i}`}
                        placeholder="Codice"
                        value={o.code || ""}
                        onChange={(e) => {
                          const arr = [...form.offers]; arr[i] = { ...arr[i], code: e.target.value.toUpperCase() };
                          setForm({ ...form, offers: arr });
                        }}
                        className="bg-zinc-900 border-zinc-800 h-9 text-sm w-24 font-mono uppercase"
                      />
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, offers: form.offers.filter((_, j) => j !== i) })}
                        data-testid={`sponsor-offer-delete-${i}`}
                        className="px-2 rounded-lg border border-red-800/50 text-red-400 hover:bg-red-500/10 text-xs"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                    <Input
                      data-testid={`sponsor-offer-desc-${i}`}
                      placeholder="Descrizione breve dell'offerta"
                      value={o.description || ""}
                      onChange={(e) => {
                        const arr = [...form.offers]; arr[i] = { ...arr[i], description: e.target.value };
                        setForm({ ...form, offers: arr });
                      }}
                      className="bg-zinc-900 border-zinc-800 h-8 text-xs"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Contatto in-app */}
            <div className="border-t border-zinc-800 pt-3">
              <label className="flex items-start gap-2" data-testid="sponsor-contact-row">
                <Checkbox data-testid="sponsor-contact-enabled" checked={form.contact_enabled} onCheckedChange={(v) => setForm({ ...form, contact_enabled: !!v })} className="mt-0.5" />
                <span className="text-xs text-zinc-300">
                  <b>📨 Permetti al cliente di contattarti dall'app</b><br/>
                  <span className="text-zinc-400">Il cliente potrà inviare una richiesta direttamente dallo sponsor: riceverai email + WhatsApp con nome, telefono e messaggio.</span>
                </span>
              </label>
              {form.contact_enabled && (
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <Input
                    data-testid="sponsor-contact-email"
                    placeholder="Email destinatario*"
                    type="email"
                    value={form.contact_email}
                    onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                    className="bg-zinc-950 border-zinc-800 h-9 text-sm"
                  />
                  <Input
                    data-testid="sponsor-contact-phone"
                    placeholder="Telefono WhatsApp (opz.)"
                    value={form.contact_phone}
                    onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                    className="bg-zinc-950 border-zinc-800 h-9 text-sm"
                  />
                </div>
              )}
            </div>

            {/* Push notification al salvataggio */}
            <label className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/5 border border-amber-500/30" data-testid="sponsor-notify-row">
              <Checkbox data-testid="sponsor-notify-push" checked={form.notify_push} onCheckedChange={(v) => setForm({ ...form, notify_push: !!v })} className="mt-0.5" />
              <span className="text-xs text-zinc-300">
                <b className="text-amber-200">📱 Invia notifica push sull'app al salvataggio</b><br/>
                <span className="text-zinc-400">Tutti i clienti target riceveranno immediatamente una push "📢 {form.name || 'nome sponsor'}" con la descrizione impostata.</span>
              </span>
            </label>

            <div className="flex gap-2 pt-2">
              <Button data-testid="sponsor-cancel" variant="outline" onClick={() => setEditOpen(false)} className="border-zinc-800 flex-1">Annulla</Button>
              <Button data-testid="sponsor-save" onClick={save} className="bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)] flex-1">Salva</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Storico contatti ricevuti per lo sponsor */}
      <Dialog open={!!contactsFor} onOpenChange={(o) => { if (!o) { setContactsFor(null); setContacts([]); setContactFilter("all"); setExportRange({ from: "", to: "" }); load(); } }}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="sponsor-contacts-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-blue-300" /> Richieste ricevute — {contactsFor?.name}
            </DialogTitle>
          </DialogHeader>
          {contacts.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 pb-2 border-b border-zinc-800 justify-between" data-testid="sponsor-contacts-toolbar">
              <div className="flex gap-2 flex-wrap" data-testid="sponsor-contacts-tabs">
                {[
                  { k: "all", label: "Tutte", cls: "bg-zinc-700 text-white border-zinc-600" },
                  { k: "new", label: "Nuove", cls: "bg-blue-500/20 text-blue-300 border-blue-500/40" },
                  { k: "in_progress", label: "In lavorazione", cls: "bg-amber-500/20 text-amber-300 border-amber-500/40" },
                  { k: "closed", label: "Chiuse", cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" },
                ].map((t) => {
                  const count = t.k === "all"
                    ? contacts.length
                    : contacts.filter((c) => (c.status || "new") === t.k).length;
                  const on = contactFilter === t.k;
                  return (
                    <button
                      key={t.k}
                      type="button"
                      onClick={() => setContactFilter(t.k)}
                      data-testid={`sponsor-contacts-tab-${t.k}`}
                      className={`text-[11px] px-3 py-1 rounded-full border transition ${on ? t.cls : "border-zinc-800 text-zinc-500 hover:border-zinc-600"}`}
                    >
                      {t.label} <span className="opacity-70 ml-1">({count})</span>
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <input
                  type="date"
                  value={exportRange.from}
                  onChange={(e) => setExportRange({ ...exportRange, from: e.target.value })}
                  data-testid="sponsor-contacts-export-from"
                  className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-[10px] text-zinc-300"
                  title="Da"
                />
                <span className="text-[10px] text-zinc-500">→</span>
                <input
                  type="date"
                  value={exportRange.to}
                  onChange={(e) => setExportRange({ ...exportRange, to: e.target.value })}
                  data-testid="sponsor-contacts-export-to"
                  className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-[10px] text-zinc-300"
                  title="A"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={exportContacts}
                  disabled={exporting}
                  data-testid="sponsor-contacts-export-xlsx"
                  className="border-emerald-800/50 text-emerald-300 hover:bg-emerald-500/10 h-7 text-[11px]"
                  title="Esporta Excel"
                >
                  {exporting === "xlsx" ? "⏳" : "📥"} XLSX
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => exportContacts("pdf")}
                  disabled={!!exporting}
                  data-testid="sponsor-contacts-export-pdf"
                  className="border-red-800/50 text-red-300 hover:bg-red-500/10 h-7 text-[11px]"
                  title="Esporta PDF stampabile"
                >
                  {exporting === "pdf" ? "⏳" : "📄"} PDF
                </Button>
              </div>
            </div>
          )}
          {contacts.length === 0 ? (
            <div className="text-center py-8 text-zinc-500 text-sm" data-testid="sponsor-contacts-empty">
              Nessuna richiesta ricevuta finora.
            </div>
          ) : (() => {
            const filtered = contactFilter === "all"
              ? contacts
              : contacts.filter((c) => (c.status || "new") === contactFilter);
            if (filtered.length === 0) {
              return (
                <div className="text-center py-8 text-zinc-500 text-sm" data-testid="sponsor-contacts-filtered-empty">
                  Nessuna richiesta in questo stato.
                </div>
              );
            }
            return (
            <div className="space-y-2" data-testid="sponsor-contacts-list">
              {filtered.map((c) => {
                const draft = contactDrafts[c.id] || { notes: c.notes || "", status: c.status || "new" };
                const meta = STATUS_META[draft.status] || STATUS_META.new;
                const dirty = (c.notes || "") !== (draft.notes || "") || (c.status || "new") !== (draft.status || "new");
                return (
                <div key={c.id} data-testid={`sponsor-contact-${c.id}`} className="rounded-lg bg-zinc-950 border border-zinc-800 p-3">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="font-heading font-bold text-sm truncate">{c.customer_name}</div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${meta.cls}`} data-testid={`sponsor-contact-status-badge-${c.id}`}>
                      {meta.label}
                    </span>
                  </div>
                  <div className="text-[10px] text-zinc-500 mb-1" data-testid={`sponsor-contact-dates-${c.id}`}>
                    Ricevuta: {new Date(c.created_at).toLocaleString("it-IT")}
                    {c.updated_at && <> · Ultimo agg.: <span className="text-zinc-300">{new Date(c.updated_at).toLocaleString("it-IT")}</span></>}
                  </div>
                  <div className="text-[11px] text-zinc-500 font-mono mb-2">
                    {c.customer_phone || "—"} · {c.customer_email || "—"}
                  </div>
                  {c.offer_title && (
                    <div className="inline-block text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 mb-2">
                      🎁 {c.offer_title}
                    </div>
                  )}
                  <div className="text-xs text-zinc-300 italic border-l-2 border-blue-500/50 pl-2 mb-3">
                    "{c.message}"
                  </div>

                  <div className="grid gap-2 mb-2">
                    <div className="flex gap-1" data-testid={`sponsor-contact-status-${c.id}`}>
                      {Object.entries(STATUS_META).map(([k, v]) => {
                        const on = draft.status === k;
                        return (
                          <button
                            key={k}
                            type="button"
                            onClick={() => patchDraft(c.id, { status: k })}
                            data-testid={`sponsor-contact-status-${c.id}-${k}`}
                            className={`text-[10px] px-2 py-1 rounded-full border transition ${on ? v.cls : "border-zinc-800 text-zinc-500 hover:border-zinc-600"}`}
                          >
                            {v.label}
                          </button>
                        );
                      })}
                    </div>
                    <Textarea
                      data-testid={`sponsor-contact-notes-${c.id}`}
                      value={draft.notes}
                      onChange={(e) => patchDraft(c.id, { notes: e.target.value })}
                      placeholder="Note interne di lavorazione (visibili solo all'admin)"
                      rows={2}
                      className="bg-zinc-900 border-zinc-800 text-xs"
                    />
                    <div className="flex justify-end">
                      <Button
                        data-testid={`sponsor-contact-save-${c.id}`}
                        size="sm"
                        onClick={() => saveContact(c)}
                        disabled={!dirty || savingContactId === c.id}
                        className="bg-blue-600 hover:bg-blue-700 h-7 text-[11px]"
                      >
                        {savingContactId === c.id ? "Salvo…" : "💾 Salva"}
                      </Button>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    {c.customer_phone && (
                      <a
                        href={`https://wa.me/${c.customer_phone.replace(/[^\d+]/g, "")}?text=${encodeURIComponent(`Ciao ${c.customer_name}, riguardo la tua richiesta`)}`}
                        target="_blank" rel="noreferrer"
                        className="text-[11px] text-emerald-300 hover:text-emerald-200 flex items-center gap-1"
                      >
                        <MessageSquare className="h-3 w-3" /> Rispondi WhatsApp
                      </a>
                    )}
                    {c.customer_email && (
                      <a
                        href={`mailto:${c.customer_email}?subject=Re: richiesta sponsor`}
                        className="text-[11px] text-blue-300 hover:text-blue-200 flex items-center gap-1"
                      >
                        📧 Rispondi email
                      </a>
                    )}
                  </div>
                </div>
              );})}
            </div>
          );})()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
